// The typographic cover, for books with no cover image.
//
// THE one fallback for the library. Three divergent inline versions of this idea
// already exist (Library.tsx, Marketplace.tsx, AuthorPage.tsx) — this replaces
// the library's, and the other two should be retired onto it separately.
//
// It has to hold its own at ~150px in a seven-up rail and in a six-column wall,
// because the entire page is covers: a cover-shaped hole is far more damaging
// here than on the old four-up grid. So it is set like a real jacket — ruled
// frame, ex-libris rule, title in Cormorant, byline in mono — rather than a grey
// box with text in it.

import type { ShelfBook } from "../../lib/library-types";

export type PlateSize = "hero" | "companion" | "rail" | "wall" | "row";

/** Type scale per slot. A 62px row thumbnail can't carry a 20px title. */
const SCALE: Record<PlateSize, { title: string; byline: string; pad: string; rule: string }> = {
  hero:      { title: "text-[26px] leading-[1.1]",  byline: "text-[10px]", pad: "p-5",   rule: "my-3" },
  companion: { title: "text-[18px] leading-[1.12]", byline: "text-[9px]",  pad: "p-3.5", rule: "my-2" },
  rail:      { title: "text-[15px] leading-[1.14]", byline: "text-[8.5px]", pad: "p-3",  rule: "my-2" },
  wall:      { title: "text-[13px] leading-[1.15]", byline: "text-[8px]",  pad: "p-2.5", rule: "my-1.5" },
  row:       { title: "text-[9px]  leading-[1.15]", byline: "hidden",      pad: "p-1",   rule: "hidden" },
};

export function CoverPlate({ book, size }: { book: ShelfBook; size: PlateSize }) {
  const scale = SCALE[size];
  return (
    <div
      className={`flex h-full w-full flex-col items-center justify-center bg-paper-deep text-center ${scale.pad}`}
      aria-hidden="true"
    >
      <div className={`font-serif text-ink-body ${scale.title} line-clamp-4`}>{book.title}</div>
      {scale.rule !== "hidden" && (
        <div className={`h-px w-8 bg-terracotta ${scale.rule}`} />
      )}
      {book.author_name && scale.byline !== "hidden" && (
        <div className={`font-mono uppercase tracking-smallcaps text-ink-faint ${scale.byline} line-clamp-2`}>
          {book.author_name}
        </div>
      )}
    </div>
  );
}
